import * as React from "react";
import { createPortal } from "react-dom";
import { DayPicker, type DateRange } from "react-day-picker";
import { CalendarRange, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  addDays,
  formatDay,
  isSameDay,
  parseValue,
  toLocalDate,
  todayIn,
  toValue,
  type CalDate,
} from "@/lib/dates";
import { usePopover } from "./usePopover";

/**
 * One control for "which days am I looking at": presets down the left, a
 * calendar on the right.
 *
 * ── Why the presets and the calendar are one control ────────────────────────
 *
 * The obvious build is a "Last N days" dropdown beside two date fields, and it
 * has a state nobody can read: pick a preset and the two fields still say
 * something else, so the page is filtered by one of them and the reader has to
 * guess which. They are one question, so they get one answer and one trigger.
 * Choosing a preset moves the calendar under it; choosing days drops the preset
 * and the trigger reads "25 Jun – 26 Jun".
 *
 * ── Every date here is computed in the caller's chosen zone ─────────────────
 *
 * `timeZone` is the **site's**, not the browser's, once a caller has one to
 * pass. "Today" in Pacific/Midway is a different day from today in Hyderabad for
 * most of the day, and an assessment belongs to the day the crew was standing
 * in. `lib/dates.ts` has the full argument, including the `new Date("…")`
 * off-by-one this avoids.
 *
 * ── Not wired to anything yet ──────────────────────────────────────────────
 *
 * Deliberately. The assessment feed it belongs on is Phase 1 and the API has no
 * date filter to send this to, so wiring it now would mean a control that
 * silently changes nothing. It is built ahead of that because the selection
 * logic below is the part with the bugs in it, and it is better reviewed on its
 * own than as one paragraph of a feature diff.
 */

export interface DateRangeValue {
  /** A preset id, or "" for a hand-picked range. */
  preset: string;
  /** "YYYY-MM-DD" in the caller's zone. Set only for a hand-picked range. */
  from: string;
  to: string;
}

interface Preset {
  id: string;
  label: string;
  /** Inclusive of the last day: "last 7 days" is today plus the six before it. */
  days: number;
  endsYesterday?: boolean;
}

const RANGE_PRESETS: readonly Preset[] = [
  { id: "today", label: "Today", days: 1 },
  { id: "yesterday", label: "Yesterday", days: 1, endsYesterday: true },
  { id: "7d", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
];

/**
 * A picked value as two calendar days.
 *
 * Selecting a preset emits `{ preset: "30d", from: "", to: "" }` - the id, not
 * the days it stands for, because "last 30 days" has to keep meaning that when
 * the page is still open tomorrow. Every consumer therefore has to resolve it,
 * and the one that did not silently sent no date filter at all: the visits list
 * offered five presets and none of them changed the query.
 *
 * It lives beside `RANGE_PRESETS` rather than in `lib/dates.ts` because it is
 * only meaningful against that table, and re-exporting the table somewhere else
 * to move one function is how two copies of it start.
 */
export function resolveRange(
  value: DateRangeValue,
  timeZone?: string | null
): { from: CalDate | null; to: CalDate | null } {
  if (!value.preset) return { from: parseValue(value.from), to: parseValue(value.to) };

  const preset = RANGE_PRESETS.find((candidate) => candidate.id === value.preset);
  if (!preset) return { from: null, to: null };

  const today = todayIn(timeZone);
  const last = preset.endsYesterday ? addDays(today, -1) : today;
  return { from: addDays(last, -(preset.days - 1)), to: last };
}

export function DateRangeFilter({
  value,
  onChange,
  timeZone,
  defaultPreset = "7d",
  presetIds,
  disableFuture = true,
  label = "Date range",
  className,
}: {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  /** The site's IANA zone, ideally. Every "today" below is today *there*. */
  timeZone?: string | null;
  /**
   * The window the page starts on. While it is selected the trigger reads as
   * unset rather than as a filter somebody applied, so it neither highlights nor
   * counts towards a `FilterBar`'s active count.
   */
  defaultPreset?: string;
  /** Restrict which presets are offered, in their normal order. */
  presetIds?: readonly string[];
  /**
   * Block picking a day after today. On for "when did it happen" filters, which
   * is all of them in this product so far; off for anything forward-looking.
   */
  disableFuture?: boolean;
  label?: string;
  className?: string;
}) {
  const { open, toggle, close, rootRef, triggerRef, panelRef, panelStyle, panelTarget } =
    usePopover({
      align: "start",
    });

  const presets = presetIds
    ? RANGE_PRESETS.filter((preset) => presetIds.includes(preset.id))
    : RANGE_PRESETS;

  /** The first click of a new range. Null once the range is committed. */
  const [anchor, setAnchor] = React.useState<Date | null>(null);
  const [hover, setHover] = React.useState<Date | null>(null);
  /**
   * The month on screen, controlled.
   *
   * `defaultMonth` seeds only the first render, so picking "Last 30 days" while
   * looking at a later month left the calendar where it was, showing no
   * selection at all - the range was there, just off screen.
   */
  const [month, setMonth] = React.useState<Date | null>(null);

  const today = React.useMemo(() => todayIn(timeZone), [timeZone]);
  const todayDate = toLocalDate(today);

  const presetRange = React.useCallback(
    (id: string): DateRange | undefined => {
      const { from, to } = resolveRange({ preset: id, from: "", to: "" }, timeZone);
      if (!from || !to) return undefined;
      return { from: toLocalDate(from), to: toLocalDate(to) };
    },
    [timeZone]
  );

  const customFrom = parseValue(value.from);
  const customTo = parseValue(value.to);

  const selected: DateRange | undefined = value.preset
    ? presetRange(value.preset)
    : customFrom
      ? { from: toLocalDate(customFrom), to: customTo ? toLocalDate(customTo) : undefined }
      : undefined;

  // Reset the half-finished state on close, and land on the month the selection
  // is in next time rather than wherever the user had browsed to.
  React.useEffect(() => {
    if (open) return;
    setAnchor(null);
    setHover(null);
    setMonth(null);
  }, [open]);

  /**
   * Follow the selection while the panel is open, so choosing a preset is
   * visible the instant it happens rather than the next time it is opened.
   * Keyed on the value rather than on `selected`, so manual month navigation
   * still works - this fires only when the answer itself changes.
   */
  React.useEffect(() => {
    if (!open) return;
    const end = selected?.to ?? selected?.from;
    if (end) setMonth(new Date(end.getFullYear(), end.getMonth(), 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value.preset, value.from, value.to]);

  function pickPreset(id: string) {
    setAnchor(null);
    setHover(null);
    onChange({ preset: id, from: "", to: "" });
    // The panel stays open: you may want to see what the preset chose, or nudge
    // one end of it without starting again.
  }

  function commit(from: Date, to: Date) {
    const [start, end] = from <= to ? [from, to] : [to, from];
    onChange({ preset: "", from: toValue(start), to: toValue(end) });
  }

  /**
   * Selection by hand rather than through DayPicker's own range mode, which
   * treats a click inside an existing range as *shrinking* it and gives no
   * feedback at all between the two clicks.
   *
   * Three cases and only three:
   *
   *   * Mid-pick, an anchor is down → this click closes the range, either order.
   *   * A range exists and you click one of its two ends → you have grabbed that
   *     end: the *other* end becomes the anchor and the next click re-places the
   *     one you grabbed. One end moves, the other stays exactly where it was.
   *   * Anything else → start a fresh range from the day clicked.
   *
   * The tempting rule - "move whichever end is nearer" - looked clever and
   * behaved badly. With 10–20 selected, clicking 15 then 17 gave 17–20 rather
   * than 15–17, because the second click was nearer the start it had just moved.
   * Restarting on a plain click is predictable; grabbing an end is explicit.
   */
  function onDayClick(day: Date, modifiers: { disabled?: boolean }) {
    if (modifiers.disabled) return;

    if (anchor) {
      setAnchor(null);
      setHover(null);
      commit(anchor, day);
      return;
    }

    const from = selected?.from;
    const to = selected?.to;

    if (from && to && !isSameDay(from, to)) {
      if (isSameDay(day, from)) {
        setAnchor(to);
        setHover(null);
        return;
      }
      if (isSameDay(day, to)) {
        setAnchor(from);
        setHover(null);
        return;
      }
    }

    setAnchor(day);
    setHover(null);
  }

  // While an anchor is down the band follows the pointer, so a two-click range
  // is not two clicks with nothing in between them.
  const preview: DateRange | undefined = anchor
    ? hover && hover < anchor
      ? { from: hover, to: anchor }
      : { from: anchor, to: hover ?? anchor }
    : undefined;

  const shown = preview ?? selected;
  const custom = value.preset === "" && Boolean(value.from || value.to);

  /**
   * A hand-picked range carries `preset: ""` and its dates in `from`/`to`. On a
   * page whose default is also "" the preset alone cannot tell a real selection
   * from the default, so the dates have to count too - otherwise a picked range
   * never highlights.
   */
  const active = value.preset !== defaultPreset || custom;

  const triggerLabel = value.preset
    ? (RANGE_PRESETS.find((preset) => preset.id === value.preset)?.label ?? label)
    : shown?.from && shown?.to
      ? `${formatDay(shown.from)} – ${formatDay(shown.to)}`
      : shown?.from
        ? `${formatDay(shown.from)} – …`
        : label;

  return (
    <div ref={rootRef} className={cn("fsel", className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        onClick={toggle}
        className={cn("fsel__trigger", "drange__trigger", active && "fsel__trigger--active")}
      >
        <span className="fsel__icon">
          <CalendarRange />
        </span>
        <span className="fsel__value">{triggerLabel}</span>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            // See the "top layer" note in `usePopover.ts` - this is what lets
            // the panel render over an open `Modal`/`Drawer`.
            popover="manual"
            role="dialog"
            aria-label={label}
            style={panelStyle}
            className="fsel__panel drange__panel"
          >
            <div className="drange__presets">
              {presets.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  aria-pressed={value.preset === preset.id}
                  onClick={() => pickPreset(preset.id)}
                  className={cn(
                    "drange__preset",
                    value.preset === preset.id && "drange__preset--on"
                  )}
                >
                  {preset.label}
                </button>
              ))}

              {/* Not a button. It is the state you are already in, and there is
                  nothing to switch to - a clickable "Custom" that does nothing
                  is worse than a label that says where you are. */}
              {custom && <span className="drange__preset drange__preset--on">Custom</span>}
            </div>

            <div className="drange__calendar" onMouseLeave={() => setHover(null)}>
              <DayPicker
                mode="range"
                selected={shown}
                /* `selected` is only DayPicker's *initial* value unless an
                   `onSelect` is present: without one it keeps its own internal
                   range and ignores the prop, so picking a preset did nothing
                   until the panel was closed and reopened (a remount). Passing
                   this makes it controlled - our state is the only state. The
                   handler is empty because `onDayClick` owns the logic. */
                onSelect={() => {}}
                month={month ?? shown?.to ?? shown?.from ?? todayDate}
                onMonthChange={setMonth}
                onDayClick={onDayClick}
                onDayMouseEnter={(day) => anchor && setHover(day)}
                disabled={disableFuture ? { after: todayDate } : undefined}
                formatters={{
                  formatWeekdayName: (day) =>
                    new Intl.DateTimeFormat(undefined, { weekday: "narrow" }).format(day),
                }}
                components={{
                  Chevron: ({ orientation }) =>
                    orientation === "left" ? <ChevronLeft /> : <ChevronRight />,
                }}
              />
            </div>

            <div className="drange__footer">
              <button type="button" className="fsel__done" onClick={() => close(true)}>
                Done
              </button>
            </div>
          </div>,
          panelTarget
        )}
    </div>
  );
}
